import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, X, Loader2, Images } from "lucide-react";
import SEO from "../components/SEO";
import api from "../lib/api";

export default function PhotoGalleryPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get("/gallery/photos")
      .then((r) => setPhotos(Array.isArray(r.data?.photos) ? r.data.photos : []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--uhel-900)", color: "var(--text-svetly)" }} data-testid="photo-gallery-page">
      <SEO title="Fotogalerie" description="Kompletní fotogalerie prací Apex Detailing." path="/fotogalerie" />
      <div className="relative overflow-hidden" style={{ borderBottom: "0.5px solid var(--linka-tmava)" }}>
        <div className="lit-gallery absolute inset-0 pointer-events-none" />
        <div className="relative max-w-[1180px] mx-auto px-[18px] md:px-8 pt-10 pb-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-tlum-dark hover:text-akcent transition-colors" data-testid="photo-gallery-back-home">
            <ArrowLeft className="w-4 h-4" /> Zpět na hlavní stránku
          </Link>
          <h1 className="t-h1 mt-5 text-krem-100" style={{ fontSize: "clamp(37.2px, 6.138vw, 61.38px)" }}>Fotogalerie</h1>
          <p className="t-body mt-3 text-tlum-dark max-w-xl">Všechny fotky z naší práce na jednom místě.</p>
        </div>
      </div>

      <div className="max-w-[1180px] mx-auto px-[18px] md:px-8 py-12 md:py-16">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-akcent" /></div>
        ) : photos.length === 0 ? (
          <div className="text-center py-20 text-tlum-dark" data-testid="photo-gallery-empty">
            <Images className="w-10 h-10 mx-auto mb-3 text-tlum-dark" />
            Zatím zde nejsou žádné fotky.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="photo-gallery-grid">
            {photos.map((p) => (
              <button
                key={p.id}
                onClick={() => setLightbox(p)}
                className="relative aspect-square overflow-hidden rounded-xl group"
                style={{ border: "0.5px solid var(--linka-tmava)" }}
                data-testid={`photo-gallery-item-${p.id}`}
              >
                <img src={p.url} alt={p.caption || "Fotka z naší práce"} className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-10"
            style={{ background: "rgba(12,12,11,0.92)" }}
            onClick={() => setLightbox(null)}
            data-testid="photo-gallery-lightbox"
          >
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-5 right-5 w-10 h-10 rounded-full flex items-center justify-center text-krem-100 hover:text-akcent transition-colors"
              style={{ border: "0.5px solid var(--linka-tmava)" }}
              data-testid="photo-gallery-lightbox-close"
              aria-label="Zavřít"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.img
              initial={{ scale: 0.95 }} animate={{ scale: 1 }}
              src={lightbox.url}
              alt={lightbox.caption || "Fotka z naší práce"}
              className="max-h-[85vh] max-w-full rounded-xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            {lightbox.caption && (
              <div className="absolute bottom-6 left-0 right-0 text-center text-krem-100/85 text-sm">{lightbox.caption}</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
